import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Disc3 } from "lucide-react";
import { ListenButton } from "@/components/ListenButton";
import api from "@/lib/api";

const CHARTS = [["uk_singles", "UK Singles"], ["uk_albums", "UK Albums"], ["us_singles", "US Singles"], ["us_albums", "US Albums"]];

const Cell = ({ n, chart, yrs }) => {
  if (!n) return <span className="text-[12px] text-white/30">—</span>;
  return (
    <span className="flex items-start gap-2 min-w-0" data-testid={`no1-cell-${chart}-${yrs}`}>
      <span className="min-w-0 flex-1">
        <span className="block text-[14px] font-semibold leading-snug truncate" title={n.title}>{n.title}</span>
        <span className="block text-[12px] text-white/70 truncate" title={n.artist}>{n.artist}</span>
      </span>
      <ListenButton artist={n.artist} title={n.title} dark testid={`no1-listen-${chart}-${yrs}`} />
    </span>
  );
};

export const NumberOnesSection = ({ ddmm }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!ddmm) return;
    Promise.all(CHARTS.map(([k]) => api.get("/number-ones/on-this-day", { params: { date: ddmm, chart: k } }).then((r) => [k, r.data.items]).catch(() => [k, []])))
      .then((pairs) => setData(Object.fromEntries(pairs)));
  }, [ddmm]);

  if (data === null) return null;
  const years = [...new Set(CHARTS.flatMap(([k]) => (data[k] || []).map((n) => n.years_ago)))].sort((a, b) => a - b);
  if (years.length === 0) return null;
  const at = (k, yrs) => (data[k] || []).find((n) => n.years_ago === yrs);
  const yearOf = (yrs) => CHARTS.map(([k]) => at(k, yrs)).find(Boolean)?.year;

  return (
    <section id="number-ones" data-testid="number-ones-section" className="mt-10">
      <div className="relative overflow-hidden rounded-2xl border border-ink bg-ink text-white px-6 py-7 md:px-10 md:py-9">
        <div className="absolute -right-16 -bottom-16 w-64 h-64 rounded-full border-[18px] border-gold/10 pointer-events-none" />
        <div className="absolute -right-6 -bottom-6 w-44 h-44 rounded-full border-[10px] border-gold/10 pointer-events-none" />
        <div className="relative">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <Disc3 className="w-4 h-4 text-gold" />
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold">Music · No.1 on this day</p>
              </div>
              <h2 className="mt-2 text-2xl md:text-3xl font-bold leading-tight">What was topping the charts</h2>
              <p className="mt-1 text-[14px] text-white/70">The official No.1 singles and albums in the UK and the USA on this date, decade by decade.</p>
            </div>
            <div className="flex flex-wrap gap-2 self-start md:self-auto">
              <Link to="/quiz" data-testid="number-ones-quiz-link" className="inline-flex items-center gap-2 px-5 h-11 rounded-full border border-gold text-gold text-sm font-semibold hover:bg-gold hover:text-ink transition-colors">Play the History Quiz</Link>
              <Link to="/number-ones" data-testid="number-ones-all-link" className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-gold text-ink text-sm font-semibold hover:bg-white transition-colors">No.1 this week, every year →</Link>
            </div>
          </div>

          {/* Desktop grid */}
          <div className="mt-6 hidden md:block" data-testid="no1-grid">
            <div className="grid grid-cols-[88px_repeat(4,minmax(0,1fr))] gap-x-4 border-b border-white/15 pb-2">
              <span className="text-[10px] uppercase tracking-[0.15em] text-white/50">Year</span>
              {CHARTS.map(([k, label]) => <span key={k} className="text-[10px] uppercase tracking-[0.15em] text-gold font-bold">{label}</span>)}
            </div>
            {years.map((yrs) => (
              <div key={yrs} data-testid={`no1-row-${yrs}`} className="grid grid-cols-[88px_repeat(4,minmax(0,1fr))] gap-x-4 items-start py-3 border-b border-white/10 last:border-0">
                <span>
                  <span className="block text-[22px] font-bold text-gold leading-none">{yearOf(yrs)}</span>
                  <span className="block text-[10px] uppercase tracking-wider text-white/50 mt-1">{yrs} yrs ago</span>
                </span>
                {CHARTS.map(([k]) => <Cell key={k} n={at(k, yrs)} chart={k} yrs={yrs} />)}
              </div>
            ))}
          </div>

          {/* Mobile stacked */}
          <ul className="mt-6 space-y-3 md:hidden" data-testid="no1-stack">
            {years.map((yrs) => (
              <li key={yrs} className="rounded-xl border border-white/10 bg-white/5 px-4 py-3">
                <p className="text-[18px] font-bold text-gold leading-none">{yearOf(yrs)} <span className="text-[10px] uppercase tracking-wider text-white/50 font-semibold">{yrs} yrs ago</span></p>
                <ul className="mt-2 space-y-2">
                  {CHARTS.map(([k, label]) => at(k, yrs) && (
                    <li key={k} className="flex items-start gap-2">
                      <span className="w-[76px] shrink-0 text-[10px] font-bold uppercase tracking-wider text-gold pt-0.5">{label}</span>
                      <Cell n={at(k, yrs)} chart={k} yrs={`m-${yrs}`} />
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};
